import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  UserActivityProvider,
  NotificationProvider,
  useContentShareState,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { VideoMeetingFrameContainer } from "@platform/features/videoMeeting";
import { VideoMeetingErrorBoundary } from "@platform/features/videoMeeting/components/VideoMeetingErrorBoundary";
import MeetingStatusNotifier from "@platform/features/video/containers/MeetingStatusNotifier";
import { DevicePermissionInitializer } from "@platform/features/video/components/DevicePermissionInitializer";
import { useFetchActions, ACTION_TYPE_SLUG } from "@core/features/actions";
import { useScopedProgramOverride } from "@core/features/chat";
import {
  usePlatformStore,
  useVideoSession,
  useVideoMeetingDetails,
} from "@platform/store";
import { VideoMeetingLoading } from "@platform/features/videoMeeting";
import { useDeviceRefresh } from "@platform/features/video/hooks/useDeviceRefresh";
import { useScreenWakeLock } from "@platform/features/video/hooks/useScreenWakeLock";
import { VIDEO_API } from "@core/features/videoMeeting/videoMeeting.constants";
import { getConfig } from "@core/services/api/config";
import { getAuthToken } from "@core/services/api/getAuthToken";
import type { MeetingDetails } from "@core/features/videoMeeting";

export function VideoMeetingPage() {
  const navigate = useNavigate();
  const { meetingId: meetingIdFromUrl } = useParams();
  const { isInitialized } = useVideoSession();
  const { meetingId, meetingDetails } = useVideoMeetingDetails();
  const user = usePlatformStore((s) => s.user);
  const { sharingAttendeeId } = useContentShareState();
  const meetingManager = useMeetingManager();

  useDeviceRefresh();
  useScreenWakeLock(isInitialized);

  // Scope chat to the meeting's program (not the user's first-enrolled one).
  // See docs/chat-architecture.md §2 for the override mechanism.
  const setProgramIdOverride = usePlatformStore((s) => s.setProgramIdOverride);
  useScopedProgramOverride(meetingDetails?.program ?? null, setProgramIdOverride);

  // Prevent iOS Safari pull-to-refresh from reloading the page during meetings.
  // Must target <body> — overscroll-behavior on child divs has no effect.
  // Also set theme-color to match the meeting page background (brand-900) so
  // mobile browser chrome matches the dark meeting UI instead of showing brand-50.
  useEffect(() => {
    document.body.style.overscrollBehavior = 'none';
    document.body.style.backgroundColor = '#0b1400';

    const meta = document.querySelector('meta[name="theme-color"]');
    const originalColor = meta?.getAttribute('content') ?? '';
    if (meta) meta.setAttribute('content', '#0b1400');

    return () => {
      document.body.style.overscrollBehavior = '';
      document.body.style.backgroundColor = '';
      if (meta) meta.setAttribute('content', originalColor);
    };
  }, []);

  // Close Chime WebSocket on page refresh/navigation.
  // Use fetch with keepalive for the leave API call (survives page unload
  // like sendBeacon but supports custom headers for authentication).
  const roomIdForLeave = meetingDetails?.video_rooms?.[0]?.id ?? null;

  // Refresh auth token periodically so beforeunload has a valid token.
  // Firebase tokens expire after 1 hour; refreshing every 5 minutes ensures
  // a valid token is always available for the leave request.
  const authTokenRef = useRef<string | null>(null);
  useEffect(() => {
    const refreshToken = () => {
      getAuthToken().then((token) => {
        authTokenRef.current = token || null;
      });
    };
    refreshToken();
    const interval = setInterval(refreshToken, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Guard against double-firing: desktop browsers fire beforeunload then
    // pagehide — the second call is a no-op.
    const didLeaveRef = { current: false };

    const handleLeave = (event?: Event) => {
      if (didLeaveRef.current) return;

      // `pagehide` fires both on real unload and when the page enters
      // BFCache (tab switch, home-screen, device sleep on iOS Safari).
      // `event.persisted === true` means BFCache — the page will likely
      // be restored, so firing a leave here would spuriously mark the
      // user as gone while their Chime SDK session keeps running in
      // the background. Only proceed when the page is actually being
      // discarded. `beforeunload` events don't have `persisted`, so
      // they fall through the check as before.
      if (
        event &&
        "persisted" in event &&
        (event as PageTransitionEvent).persisted
      ) {
        return;
      }

      didLeaveRef.current = true;

      if (roomIdForLeave) {
        try {
          const { apiUrl } = getConfig();
          const url = `${apiUrl}${VIDEO_API.LEAVE_ROOM}`;
          const externalId = usePlatformStore.getState().chimeExternalUserId;
          const payload = JSON.stringify({
            room_id: roomIdForLeave,
            ...(externalId ? { chime_external_user_id: externalId } : {}),
          });
          const token = authTokenRef.current;

          if (token) {
            // fetch with keepalive survives page unload and supports auth headers
            fetch(url, {
              method: "POST",
              keepalive: true,
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: payload,
            }).catch(() => {});
          } else {
            // Fallback: sendBeacon if no cached token (will likely 401 but best effort)
            navigator.sendBeacon(
              url,
              new Blob([payload], { type: "application/json" })
            );
          }
        } catch {
          // Best effort
        }
      }

      // Also try the SDK leave (may not complete before unload)
      try {
        meetingManager.leave();
      } catch {
        // Best effort
      }
    };

    // Listen to both events: beforeunload (desktop) + pagehide (iOS WebKit).
    // iOS doesn't reliably fire beforeunload on tab close/navigation.
    window.addEventListener("beforeunload", handleLeave);
    window.addEventListener("pagehide", handleLeave);
    return () => {
      window.removeEventListener("beforeunload", handleLeave);
      window.removeEventListener("pagehide", handleLeave);
    };
  }, [meetingManager, roomIdForLeave]);

  // Redirect to join if not initialized
  useEffect(() => {
    if (!isInitialized) {
      const id = meetingIdFromUrl || meetingId;
      if (id) navigate(`/video/join/${id}`);
    }
  }, [isInitialized, meetingIdFromUrl, meetingId, navigate]);

  // Fetch meeting script actions
  const { data: actionsData } = useFetchActions({
    programId: meetingDetails?.program ?? 0,
    user,
    isEnabled: !!user && !!meetingDetails?.program,
  });

  const meetingScriptAction = useMemo(
    () =>
      actionsData?.actions
        ?.filter(
          (action) =>
            action.action_type?.slug === ACTION_TYPE_SLUG.MEETING_SCRIPT
        )
        .find(
          (action) =>
            action.binding_object_id ===
            parseInt(meetingIdFromUrl || "0", 10)
        ),
    [actionsData?.actions, meetingIdFromUrl]
  );

  if (!isInitialized || !meetingDetails) {
    return <VideoMeetingLoading />;
  }

  return (
    <NotificationProvider>
      <UserActivityProvider>
          <DevicePermissionInitializer />
          <MeetingStatusNotifier />
          <VideoMeetingErrorBoundary meetingId={meetingIdFromUrl}>
            <VideoMeetingFrameContainer
              priorityBasedPolicy={true}
              sharingAttendeeId={sharingAttendeeId || ""}
              meetingScriptAction={meetingScriptAction}
              meetingDetails={meetingDetails as MeetingDetails}
            />
          </VideoMeetingErrorBoundary>
      </UserActivityProvider>
    </NotificationProvider>
  );
}

export default VideoMeetingPage;
